import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { TranslatePipe } from '@ngx-translate/core'
import { format, isToday, isYesterday } from 'date-fns'

import { AvatarComponent } from '../../../avatar/avatar.component'
import { MinutesVerboseness } from '../../../calls.service'
import { RevisionSummary } from '../editor-revisions.service'
import { formatRelativeTime } from '../utils/format-relative-time'

type RevisionCardVm = {
  id: number
  dateLabel: string
  relative: string
  authorName: string
  otherAuthorCount: number
  systemGenerated: boolean
  minutesVerboseness: MinutesVerboseness | null
  verbosenessIcon: string | null
  isCurrent: boolean
  isSelected: boolean
}

// More bars for more detail. Only Material Symbols has the 1-3 bar variants, so the template sets that font.
const VERBOSENESS_ICONS: Record<MinutesVerboseness, string> = {
  [MinutesVerboseness.CONCISE]: 'signal_cellular_0_bar',
  [MinutesVerboseness.DETAILED]: 'signal_cellular_2_bar',
  [MinutesVerboseness.EXHAUSTIVE]: 'signal_cellular_4_bar',
}

type RevisionGroupVm = {
  day: string
  revisions: RevisionCardVm[]
}

@Component({
  selector: 'app-tiptap-revision-history',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatSlideToggleModule, TranslatePipe, AvatarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './tiptap-revision-history.component.html',
  styleUrl: './tiptap-revision-history.component.scss',
})
export class TiptapRevisionHistoryComponent implements OnChanges {
  @Input() revisions: RevisionSummary[] = []
  @Input() selectedId: number | null = null
  @Input() filterSystemOnly = false
  @Input() loading = false

  @Output() select = new EventEmitter<number>()
  @Output() filterChange = new EventEmitter<boolean>()
  @Output() close = new EventEmitter<void>()

  groups: RevisionGroupVm[] = []
  hasNoResults = false

  ngOnChanges(): void {
    this.groups = this.buildGroups()
    this.hasNoResults = !this.loading && this.groups.length === 0
  }

  onToggleFilter(checked: boolean): void {
    this.filterChange.emit(checked)
  }

  private buildGroups(): RevisionGroupVm[] {
    const visibleRevisions = this.filterSystemOnly ? this.revisions.filter((revision) => revision.systemGenerated) : this.revisions

    const groups: RevisionGroupVm[] = []
    for (const revision of visibleRevisions) {
      const card = this.buildCard(revision)
      const lastGroup = groups[groups.length - 1]
      const day = this.dayLabel(new Date(revision.createdAt))
      if (lastGroup === undefined || lastGroup.day !== day) {
        groups.push({ day, revisions: [card] })
      } else {
        lastGroup.revisions.push(card)
      }
    }
    return groups
  }

  private buildCard(revision: RevisionSummary): RevisionCardVm {
    return {
      id: revision.id,
      dateLabel: format(new Date(revision.createdAt), 'd MMM yyyy, HH:mm'),
      relative: formatRelativeTime(revision.createdAt) ?? '',
      authorName: revision.authorName,
      otherAuthorCount: revision.otherAuthorCount,
      systemGenerated: revision.systemGenerated,
      minutesVerboseness: revision.minutesVerboseness,
      verbosenessIcon: revision.minutesVerboseness ? VERBOSENESS_ICONS[revision.minutesVerboseness] : null,
      isCurrent: revision.isCurrent,
      isSelected: this.selectedId === revision.id || (this.selectedId === null && revision.isCurrent),
    }
  }

  private dayLabel(date: Date): string {
    if (isToday(date)) {
      return 'Today'
    }
    if (isYesterday(date)) {
      return 'Yesterday'
    }
    return format(date, 'd MMMM yyyy')
  }
}
